import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { Bio, projects } from '../../data/constants';
import { Tilt } from 'react-tilt';
import { BlinkingCursor } from '../../utils/CommonStyles';
import ProjectCard from '../cards/ProjectCard'; // Import ProjectCard component if it's not already imported

// Animation keyframes
const scanEffect = keyframes`
  0% { background-position: 0% 0%; }
  50% { background-position: 100% 0%; }
  100% { background-position: 0% 0%; }
`;

const blinkEffect = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
`;

const typingEffect = keyframes`
  from { width: 0 }
  to { width: 100% }
`;

const matrixRain = keyframes`
  0% {
    top: -100%;
  }
  100% {
    top: 100%;
  }
`;

// Enhanced Container styling
const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 10;
  align-items: center;
  padding: 50px 0px 230px 0px;
  background: linear-gradient(
    343deg,
    rgba(15, 18, 25, 1) 0%,
    rgba(23, 26, 35, 1) 50%,
    rgba(15, 18, 25, 1) 100%
  );
  clip-path: polygon(0 0, 100% 0, 100% 100%, 30% 95%, 0 100%);
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 100%;
  max-width: 1350px;
  gap: 12px;
  padding: 0 20px;
  z-index: 0;
`;

// Style the title section
const TitleSection = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const Title = styled.h2`
  font-size: 52px;
  text-align: center;
  font-weight: 700;
  margin-top: 20px;
  margin-bottom: 0;
  color: ${({ theme }) => theme.text_primary};
  position: relative;

  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 180px;
    height: 3px;
    background: linear-gradient(90deg, transparent, ${({ theme }) => theme.primary}, transparent);
  }

  @media (max-width: 768px) {
    margin-top: 12px;
    font-size: 36px;

    &:after {
      width: 120px;
    }
  }
`;

const Desc = styled.div`
  font-size: 18px;
  text-align: center;
  max-width: 600px;
  color: ${({ theme }) => theme.text_secondary};
  margin-top: 25px;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

// Terminal window styling with enhanced features
const TerminalWindow = styled.div`
  width: 100%;
  max-width: 1200px;
  margin-top: 20px;
  margin-bottom: 0;
  border-radius: 10px;
  background-color: #1a1a1a;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.5);
  overflow: visible;
  border: 1px solid #333;
  position: relative;

  &:after {
    content: none;
  }

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background:
      linear-gradient(to bottom, transparent 0%, rgba(0, 30, 60, 0.05) 100%),
      repeating-linear-gradient(
        0deg,
        rgba(0, 0, 0, 0.1) 0px,
        rgba(0, 0, 0, 0.1) 1px,
        transparent 1px,
        transparent 2px
      );
    pointer-events: none;
    opacity: 0.3;
    border: none;
    border-bottom: none;
  }
`;

const ScanLine = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 8px;
  background: linear-gradient(to bottom, transparent, rgba(120, 170, 255, 0.15), transparent);
  animation: ${scanEffect} 5s linear infinite;
  z-index: 10;
  pointer-events: none;
`;

const MatrixBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  z-index: 0;
  opacity: 0.05;
  pointer-events: none;
  border: none;
  outline: none;
`;

const MatrixColumn = styled.div`
  position: absolute;
  top: -100%;
  width: 2px;
  height: 100%;
  background: linear-gradient(to bottom, transparent, ${({ theme }) => theme.primary}, transparent);
  animation: ${matrixRain} ${props => props.speed || '15s'} linear infinite;
  animation-delay: ${props => props.delay || '0s'};
  left: ${props => props.position || '0%'};
`;

const TerminalHeader = styled.div`
  display: flex;
  align-items: center;
  background-color: #262626;
  padding: 12px 20px;
  border-bottom: 1px solid #333;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  position: relative;
  z-index: 1;
`;

const TerminalDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 8px;
  background-color: ${props => props.color};
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.2);
    box-shadow: 0 0 5px ${props => props.color};
  }
`;

const TerminalTitle = styled.div`
  flex: 1;
  text-align: center;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  color: #ddd;
  letter-spacing: 1px;
`;

// Terminal navigation and controls
const TerminalNav = styled.div`
  display: flex;
  padding: 5px 15px;
  background: #222;
  border-bottom: 1px solid #333;
  position: relative;
  z-index: 1;
`;

const TerminalTab = styled.div`
  padding: 8px 16px;
  margin-right: 5px;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: ${props => (props.active ? ({ theme }) => theme.primary : '#aaa')};
  background: ${props => (props.active ? '#333' : 'transparent')};
  border-radius: 4px 4px 0 0;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    color: ${({ theme }) => theme.primary};
    background: #2a2a2a;
  }

  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: ${props => (props.active ? '100%' : '0')};
    height: 2px;
    background: ${({ theme }) => theme.primary};
    transition: width 0.3s ease;
  }
`;

const TerminalBody = styled.div`
  padding: 25px 40px;
  font-family: 'Courier New', monospace;
  max-height: none;
  overflow-y: visible;
  position: relative;
  z-index: 1;

  &::-webkit-scrollbar {
    width: 0;
    display: none;
  }

  -ms-overflow-style: none;
  scrollbar-width: none;

  border-bottom: none;
  outline: none;
  box-shadow: none;
`;

const TerminalLine = styled.div`
  margin: 10px 0;
  line-height: 1.6;
  color: #ddd;
  display: flex;

  &:before {
    content: '$ ';
    color: ${({ theme }) => theme.primary};
    font-weight: bold;
    margin-right: 10px;
  }
`;

const TypingText = styled.div`
  overflow: hidden;
  white-space: nowrap;
  margin: 0;
  letter-spacing: 0.15em;
  animation: ${typingEffect} 2s steps(30, end);
`;

const CommandOutput = styled.div`
  margin: 10px 0 20px 25px;
  padding-left: 15px;
  border-left: 2px solid ${({ theme }) => theme.primary + '70'};
  position: relative;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 30px;
  width: 100%;
  overflow: visible;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -2px;
    width: 2px;
    height: 15px;
    background-color: ${({ theme }) => theme.primary};
    animation: ${blinkEffect} 1.5s infinite;
  }

  border-bottom: none;
`;

// For the final CommandOutput with the status message
const StatusOutput = styled.div`
  margin: 10px 0 20px 25px;
  padding-left: 15px;
  border-left: 2px solid ${({ theme }) => theme.primary + '70'};
  position: relative;
  width: 100%;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -2px;
    width: 2px;
    height: 15px;
    background-color: ${({ theme }) => theme.primary};
    animation: ${blinkEffect} 1.5s infinite;
  }

  border-bottom: none;
`;

// Featured project section
const FeaturedProject = styled.div`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: 1fr 2fr;
  background: rgba(35, 35, 40, 0.6);
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 20px;
  border: 1px solid ${({ theme }) => theme.primary + '30'};
  transition: all 0.4s ease;
  position: relative;

  &:hover {
    transform: translateY(-5px);
    border-color: ${({ theme }) => theme.primary + '60'};
    box-shadow:
      0 15px 30px rgba(0, 0, 0, 0.4),
      0 0 20px ${({ theme }) => theme.primary + '20'} inset;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeaturedImageWrapper = styled.div`
  position: relative;
  overflow: hidden;
  height: 100%;

  @media (max-width: 768px) {
    height: 220px;
  }
`;

const FeaturedImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.7s ease;

  ${FeaturedProject}:hover & {
    transform: scale(1.08);
  }
`;

const FeaturedContent = styled.div`
  flex: 3;
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
`;

const FeaturedLabel = styled.div`
  font-family: 'Courier New', monospace;
  color: ${({ theme }) => theme.primary};
  font-size: 14px;
  margin-bottom: 10px;

  &:before {
    content: '// ';
    opacity: 0.7;
  }
`;

const FeaturedTitle = styled.h3`
  font-size: 26px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 15px;
  position: relative;
  display: inline-block;

  &:after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 0;
    height: 2px;
    background: ${({ theme }) => theme.primary};
    transition: width 0.4s ease;
  }

  ${FeaturedProject}:hover &:after {
    width: 100%;
  }
`;

const FeaturedDescription = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: #ccc;
  margin-bottom: 20px;
  position: relative;
  padding-left: 15px;

  &:before {
    content: '>';
    position: absolute;
    left: 0;
    color: ${({ theme }) => theme.primary};
    opacity: 0.8;
  }
`;

const FeaturedStackWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
`;

const FeaturedStack = styled.span`
  font-size: 13px;
  padding: 5px 10px;
  background: rgba(0, 0, 0, 0.2);
  color: ${({ theme }) => theme.primary};
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.primary + '30'};
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    background: rgba(0, 0, 0, 0.3);
  }
`;

const FeaturedLinks = styled.div`
  display: flex;
  gap: 15px;
`;

const FeaturedLink = styled.a`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #ddd;
  text-decoration: none;
  font-size: 14px;
  transition: all 0.3s ease;

  &:hover {
    color: ${({ theme }) => theme.primary};
    transform: translateY(-2px);
  }

  i {
    font-size: 18px;
  }
`;

const Projects = ({ openModal, setOpenModal }) => {
  const [activeView, setActiveView] = useState('all');
  const [typedCommands, setTypedCommands] = useState([]);
  const [featuredProject, setFeaturedProject] = useState(projects[0] || null);

  // Simulating the typing effect of terminal commands
  useEffect(() => {
    const commandsToType = [
      'ls -la projects/',
      'cat featured_project.json',
      'find . -name "*.js" -type f',
    ];

    const typeCommands = async () => {
      for (const cmd of commandsToType) {
        await new Promise(resolve => setTimeout(resolve, 500));
        setTypedCommands(prev => [...prev, cmd]);
      }
    };

    typeCommands();
  }, []);

  // Memoize filtered projects to avoid recalculating on every render
  const filteredProjects = useMemo(() => {
    return projects.filter(project => {
      if (activeView === 'all') return true;
      if (!project.category) return activeView === 'other'; // If no category, show in "other"

      const category = project.category.toLowerCase();
      if (activeView === 'web_dev') return category.match('web_dev');
      if (activeView === 'mobile_apps')
        return category.match('mobile_apps') || category.match('mobile_apps');
      if (activeView === 'ml_projects') return category.match('ml_projects');
      if (activeView === 'other') {
        return (
          !category.match('web_dev') &&
          !category.match('mobile_apps') &&
          !category.match('ml_projects')
        );
      }
      return true;
    });
  }, [activeView]);

  // Ensure we don't display too many projects at once
  const displayLimit = 6; // Limit number of projects displayed
  const limitedProjects = useMemo(() => {
    return filteredProjects.filter(p => p.id !== featuredProject?.id).slice(0, displayLimit);
  }, [filteredProjects, featuredProject]);

  // Change featured project based on active view
  useEffect(() => {
    // Set a featured project based on the current filter
    if (filteredProjects.length > 0) {
      setFeaturedProject(filteredProjects[0]);
    } else {
      setFeaturedProject(projects[0]);
    }
  }, [activeView, filteredProjects]);

  // Memoize the tab click handler
  const handleTabClick = useCallback(view => {
    setActiveView(view);
  }, []);

  return (
    <Container id="Projects">
      <Wrapper>
        <TitleSection>
          <Title>Projects</Title>
          <Desc>A showcase of my technical projects and contributions</Desc>
        </TitleSection>

        <TerminalWindow>
          <ScanLine />
          <MatrixBackground>
            {/* Reduced number of matrix columns from 15 to 8 for better performance */}
            {[...Array(8)].map((_, i) => (
              <MatrixColumn
                key={i}
                position={`${Math.random() * 100}%`}
                speed={`${10 + Math.random() * 20}s`}
                delay={`-${Math.random() * 10}s`}
              />
            ))}
          </MatrixBackground>

          <TerminalHeader>
            <TerminalDot color="#ff5f56" />
            <TerminalDot color="#ffbd2e" />
            <TerminalDot color="#27c93f" />
            <TerminalTitle>
              {Bio.name.toLowerCase().replaceAll(' ', '')}@backend-dev ~ /projects
            </TerminalTitle>
          </TerminalHeader>

          <TerminalNav>
            <TerminalTab
              active={activeView === 'all_projects'}
              onClick={() => handleTabClick('all_projects')}
            >
              all_projects
            </TerminalTab>
            <TerminalTab
              active={activeView === 'web_dev'}
              onClick={() => handleTabClick('web_dev')}
            >
              web_dev
            </TerminalTab>
            <TerminalTab
              active={activeView === 'mobile_apps'}
              onClick={() => handleTabClick('mobile_apps')}
            >
              mobile_apps
            </TerminalTab>
            <TerminalTab
              active={activeView === 'ml_projects'}
              onClick={() => handleTabClick('ml_projects')}
            >
              ml_projects
            </TerminalTab>
            <TerminalTab active={activeView === 'other'} onClick={() => handleTabClick('other')}>
              other
            </TerminalTab>
          </TerminalNav>
          <TerminalBody>
            {typedCommands.length > 0 && (
              <>
                <TerminalLine>
                  <TypingText>{typedCommands[0]}</TypingText>
                </TerminalLine>
                <CommandOutput>
                  {featuredProject &&
                    featuredProject.image &&
                    featuredProject.title &&
                    filteredProjects.length > 0 && (
                      <FeaturedProject>
                        <FeaturedImageWrapper>
                          <FeaturedImage
                            src={featuredProject.image}
                            alt={featuredProject.title}
                            loading="lazy"
                          />
                        </FeaturedImageWrapper>
                        <FeaturedContent>
                          <FeaturedLabel>Featured Project</FeaturedLabel>
                          <FeaturedTitle>{featuredProject.title}</FeaturedTitle>
                          <FeaturedDescription>{featuredProject.description}</FeaturedDescription>
                          <FeaturedStackWrapper>
                            {featuredProject.tags &&
                              featuredProject.tags.map((tag, idx) => (
                                <FeaturedStack key={idx}>{tag}</FeaturedStack>
                              ))}
                          </FeaturedStackWrapper>
                          <FeaturedLinks>
                            {featuredProject.github && (
                              <FeaturedLink
                                href={featuredProject.github}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <i className="fab fa-github"></i> View Code
                              </FeaturedLink>
                            )}
                          </FeaturedLinks>
                        </FeaturedContent>
                      </FeaturedProject>
                    )}

                  {limitedProjects.length > 0 &&
                    limitedProjects.map((project, index) => (
                      <Tilt key={index} options={{ max: 10, scale: 1.01, perspective: 800 }}>
                        <ProjectCard project={project} setOpenModal={setOpenModal} />
                      </Tilt>
                    ))}

                  {filteredProjects.length === 0 && (
                    <div
                      style={{
                        gridColumn: '1/-1',
                        textAlign: 'center',
                        color: '#ddd',
                        marginTop: '10px',
                      }}
                    >
                      No projects in this category yet
                    </div>
                  )}

                  {filteredProjects.length > displayLimit + 1 && (
                    <div
                      style={{
                        gridColumn: '1/-1',
                        textAlign: 'center',
                        color: '#ddd',
                        marginTop: '10px',
                      }}
                    >
                      ...more projects available on GitHub
                    </div>
                  )}
                </CommandOutput>
              </>
            )}

            {typedCommands.length > 1 && (
              <TerminalLine>
                <TypingText>echo $CURRENT_STATUS</TypingText>
              </TerminalLine>
            )}

            {typedCommands.length > 2 && (
              <StatusOutput>
                <div style={{ color: '#ddd' }}>
                  "Always building, always learning, always innovating"
                </div>
              </StatusOutput>
            )}

            <TerminalLine>
              <BlinkingCursor>_</BlinkingCursor>
            </TerminalLine>
          </TerminalBody>
        </TerminalWindow>
      </Wrapper>
    </Container>
  );
};

export default Projects;
